import { Delete, FilePresent } from '@mui/icons-material'
import { Box, Divider, Grid, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import Dropzone from 'react-dropzone'
import { Field, FormSpy, useForm } from 'react-final-form'
import { DataPanel } from '../../../components/DataPanel'
import MultistepFormField, {
    singleSelectCheckboxUtil,
} from '../../../components/MultistepForm/MultistepFormField'
import { FileAttachment } from '../../../services/models/claims'

export const PLACE_OF_TREATMENT_FIELDS = [
    'ancillary-placeOfTreatment-providersOffice',
    'ancillary-placeOfTreatment-hospital',
    'ancillary-placeOfTreatment-ECF',
    'ancillary-placeOfTreatment-Other',
]

export const TREATMENT_FROM_FIELDS = [
    'ancillary-treatmentFrom-occupational',
    'ancillary-treatmentFrom-auto',
    'ancillary-treatmentFrom-other',
]

const AncillaryStep = () => {
    const form = useForm()
    const [internalFormAttachments, setInternalFormAttachments] = useState<FileAttachment[]>([])
    const [fileStatusText, setFileStatusText] = useState<string>()
    const [hoveringOnFile, setHoveringOnFile] = useState(-1)
    const [isOrthodontics, setIsOrthodontics] = useState(
        form.getState().values['ancillary-isOrthodontics-yes'] ?? false,
    )
    const [isProsthesis, setIsProsthesis] = useState(
        form.getState().values['ancillary-isProsthesis-yes'] ?? false,
    )
    const [accidentType, setAccidentType] = useState<'AUTO' | 'OCCUPATIONAL' | 'OTHER'>()

    const handleDropAccepted = async (files: File[]) => {
        const attachments = await Promise.all(
            files.map(async (file) => {
                const arrayBuffer = await file.arrayBuffer()
                let binary = ''
                const bytes = new Uint8Array(arrayBuffer)
                for (let i = 0; i < bytes.length; i++) {
                    binary += String.fromCharCode(bytes[i])
                }
                const encodedData = btoa(binary)
                return {
                    name: file.name,
                    size: file.size,
                    data: encodedData,
                }
            }),
        )
        setInternalFormAttachments(attachments)
    }

    const onFormChange = () => {
        const values = form.getState().values

        const formFiles: FileAttachment[] = values['ancillary-attachments']
        if (
            Array.isArray(formFiles) &&
            formFiles.reduce(
                (acc, { name }, i) => (acc ? acc : name !== internalFormAttachments[i]?.name),
                false,
            )
        ) {
            setInternalFormAttachments(formFiles)
        }

        const _isOrthodontics = values['ancillary-isOrthodontics-yes'] ?? false
        if (!_isOrthodontics) {
            form.change('ancillary-dateAppliancePlaced', '')
            form.change('ancillary-monthsOfTreatmentRemaining', '')
        }
        setIsOrthodontics(_isOrthodontics)

        const _isProsthesis = values['ancillary-isProsthesis-yes'] ?? false
        if (!_isProsthesis) form.change('ancillary-datePriorPlacement', '')
        setIsProsthesis(_isProsthesis)

        const autoAccident = values['ancillary-treatmentFrom-auto'] ? 'AUTO' : false
        const occupationalAccident = values['ancillary-treatmentFrom-occupational']
            ? 'OCCUPATIONAL'
            : false
        const otherAccident = values['ancillary-treatmentFrom-other'] ? 'OTHER' : false
        const _accidentType = autoAccident || occupationalAccident || otherAccident || undefined
        if (_accidentType !== 'AUTO') form.change('ancillary-autoAccidentState', '')
        if (_accidentType !== 'OTHER' && _accidentType !== 'AUTO') {
            form.change('ancillary-accidentDate', '')
        }
        setAccidentType(_accidentType)
    }

    useEffect(() => {
        form.change('ancillary-attachments', [])
        if (internalFormAttachments.length === 0) {
            setFileStatusText('No files selected')
            return
        }
        if (internalFormAttachments.length > 10) {
            setFileStatusText('Too many files selected')
            return
        }
        const bytes = internalFormAttachments.reduce((acc, file) => acc + file.size, 0)
        if (bytes > 15_000_000) {
            setFileStatusText('File sizes exceed limit')
            return
        }
        setFileStatusText(undefined)
        form.change('ancillary-attachments', internalFormAttachments)
    }, [internalFormAttachments])

    return (
        <Box>
            <FormSpy subscription={{ values: true }} onChange={onFormChange} />
            <Field name="ancillary-attachments" render={() => <></>} />
            <DataPanel
                title="Ancillary Claim / Treatment Information"
                content={
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <Stack>
                                <Typography>Place of Treatment</Typography>
                                <MultistepFormField
                                    type="checkbox"
                                    name="ancillary-placeOfTreatment-providersOffice"
                                    label="Provider's Office"
                                    inputProps={{
                                        onChange: () =>
                                            singleSelectCheckboxUtil(
                                                form,
                                                PLACE_OF_TREATMENT_FIELDS,
                                                'ancillary-placeOfTreatment-providersOffice',
                                            ),
                                    }}
                                />
                                <MultistepFormField
                                    type="checkbox"
                                    name="ancillary-placeOfTreatment-hospital"
                                    label="Hospital"
                                    inputProps={{
                                        onChange: () =>
                                            singleSelectCheckboxUtil(
                                                form,
                                                PLACE_OF_TREATMENT_FIELDS,
                                                'ancillary-placeOfTreatment-hospital',
                                            ),
                                    }}
                                />
                                <MultistepFormField
                                    type="checkbox"
                                    name="ancillary-placeOfTreatment-ECF"
                                    label="ECF"
                                    inputProps={{
                                        onChange: () =>
                                            singleSelectCheckboxUtil(
                                                form,
                                                PLACE_OF_TREATMENT_FIELDS,
                                                'ancillary-placeOfTreatment-ECF',
                                            ),
                                    }}
                                />
                                <MultistepFormField
                                    type="checkbox"
                                    name="ancillary-placeOfTreatment-Other"
                                    label="Other"
                                    inputProps={{
                                        onChange: () =>
                                            singleSelectCheckboxUtil(
                                                form,
                                                PLACE_OF_TREATMENT_FIELDS,
                                                'ancillary-placeOfTreatment-Other',
                                            ),
                                    }}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={8}>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Stack spacing={1}>
                                        <Box sx={{ marginBottom: 1 }}>
                                            <Typography>Attachments</Typography>
                                            <Divider />
                                        </Box>
                                        {fileStatusText && (
                                            <Typography sx={{ fontStyle: 'italic' }}>
                                                {fileStatusText}
                                            </Typography>
                                        )}
                                        {internalFormAttachments.map((file, i) => (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                }}
                                                key={i}
                                                onMouseEnter={() => setHoveringOnFile(i)}
                                                onMouseLeave={() => setHoveringOnFile(-1)}
                                                onClick={() => {
                                                    const newFormAttachments: FileAttachment[] = []
                                                    internalFormAttachments.map(
                                                        (formAttachment) => {
                                                            if (
                                                                `${formAttachment.name}${formAttachment.size}` ===
                                                                `${file.name}${file.size}`
                                                            )
                                                                return
                                                            newFormAttachments.push(formAttachment)
                                                        },
                                                    )
                                                    setInternalFormAttachments(newFormAttachments)
                                                }}
                                            >
                                                {hoveringOnFile === i ? (
                                                    <Delete />
                                                ) : (
                                                    <FilePresent />
                                                )}
                                                <Typography key={i} sx={{ fontSize: 'small' }}>
                                                    {file.name}
                                                </Typography>
                                            </div>
                                        ))}
                                    </Stack>
                                </Grid>
                                <Grid item xs={5}>
                                    <Dropzone
                                        maxFiles={10}
                                        maxSize={15_000_000}
                                        onDropAccepted={handleDropAccepted}
                                    >
                                        {({ getRootProps, getInputProps }) => (
                                            <Box
                                                {...getRootProps()}
                                                style={{
                                                    borderRadius: '10px',
                                                    padding: '15px',
                                                    border: 'dashed 1px #008AEE',
                                                    display: 'flex',
                                                    alignContent: 'center',
                                                    textAlign: 'center',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <input {...getInputProps()} />
                                                <Typography typography="button">
                                                    Upload Files
                                                </Typography>
                                            </Box>
                                        )}
                                    </Dropzone>
                                </Grid>
                                <Grid item xs={3} sx={{ textAlign: 'right' }}>
                                    <Typography sx={{ fontSize: 'small' }}>
                                        Users are limited to 10 attachments and 15MB
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={4}>
                            <Box>
                                <Typography>Is Treatment for Orthodontics?</Typography>
                                <Stack direction="row" spacing={2}>
                                    <MultistepFormField
                                        type="checkbox"
                                        name="ancillary-isOrthodontics-no"
                                        label="No"
                                        inputProps={{
                                            onChange: () =>
                                                singleSelectCheckboxUtil(
                                                    form,
                                                    ['ancillary-isOrthodontics-yes'],
                                                    'ancillary-isOrthodontics-no',
                                                ),
                                        }}
                                    />
                                    <MultistepFormField
                                        type="checkbox"
                                        name="ancillary-isOrthodontics-yes"
                                        label="Yes"
                                        inputProps={{
                                            disabled: true,
                                            onChange: () =>
                                                singleSelectCheckboxUtil(
                                                    form,
                                                    ['ancillary-isOrthodontics-no'],
                                                    'ancillary-isOrthodontics-yes',
                                                ),
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box>
                                <Typography>Date Appliance Placed</Typography>
                                <MultistepFormField
                                    type="date"
                                    name="ancillary-dateAppliancePlaced"
                                    inputProps={{
                                        disabled: !isOrthodontics,
                                        disableFuture: true,
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box>
                                <Typography>Months of Treatment Remaining</Typography>
                                <MultistepFormField
                                    type="number"
                                    name="ancillary-monthsOfTreatmentRemaining"
                                    inputProps={{
                                        disabled: !isOrthodontics,
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box>
                                <Typography>Replacement of Prosthesis?</Typography>
                                <Stack direction="row" spacing={2}>
                                    <MultistepFormField
                                        type="checkbox"
                                        name="ancillary-isProsthesis-no"
                                        label="No"
                                        inputProps={{
                                            onChange: () =>
                                                singleSelectCheckboxUtil(
                                                    form,
                                                    ['ancillary-isProsthesis-yes'],
                                                    'ancillary-isProsthesis-no',
                                                ),
                                        }}
                                    />
                                    <MultistepFormField
                                        type="checkbox"
                                        name="ancillary-isProsthesis-yes"
                                        label="Yes"
                                        inputProps={{
                                            disabled: true,
                                            onChange: () =>
                                                singleSelectCheckboxUtil(
                                                    form,
                                                    ['ancillary-isProsthesis-no'],
                                                    'ancillary-isProsthesis-yes',
                                                ),
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box>
                                <Typography>Date Prior Placement</Typography>
                                <MultistepFormField
                                    type="date"
                                    name="ancillary-datePriorPlacement"
                                    inputProps={{
                                        disabled: !isProsthesis,
                                        disableFuture: true,
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4} />
                        <Grid item xs={4}>
                            <Stack>
                                <Typography>Treatment Resulting From</Typography>
                                <MultistepFormField
                                    type="checkbox"
                                    name="ancillary-treatmentFrom-occupational"
                                    label="Occupational illness / injury"
                                    inputProps={{
                                        disabled: true,
                                        onChange: () =>
                                            singleSelectCheckboxUtil(
                                                form,
                                                TREATMENT_FROM_FIELDS,
                                                'ancillary-treatmentFrom-occupational',
                                            ),
                                    }}
                                />
                                <MultistepFormField
                                    type="checkbox"
                                    name="ancillary-treatmentFrom-auto"
                                    label="Auto accident"
                                    inputProps={{
                                        disabled: true,
                                        onChange: () =>
                                            singleSelectCheckboxUtil(
                                                form,
                                                TREATMENT_FROM_FIELDS,
                                                'ancillary-treatmentFrom-auto',
                                            ),
                                    }}
                                />
                                <MultistepFormField
                                    type="checkbox"
                                    name="ancillary-treatmentFrom-other"
                                    label="Other accident"
                                    inputProps={{
                                        disabled: true,
                                        onChange: () =>
                                            singleSelectCheckboxUtil(
                                                form,
                                                TREATMENT_FROM_FIELDS,
                                                'ancillary-treatmentFrom-other',
                                            ),
                                    }}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={4} sx={{ display: 'grid', alignContent: 'end' }}>
                            <Box>
                                <Typography>Auto Accident State</Typography>
                                <MultistepFormField
                                    name="ancillary-autoAccidentState"
                                    inputProps={{ disabled: accidentType !== 'AUTO' }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={4} sx={{ display: 'grid', alignContent: 'end' }}>
                            <Box>
                                <Typography>Date of Accident</Typography>
                                <MultistepFormField
                                    type="date"
                                    name="ancillary-accidentDate"
                                    inputProps={{
                                        disabled:
                                            accidentType !== 'AUTO' && accidentType !== 'OTHER',
                                        disableFuture: true,
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                }
            />
        </Box>
    )
}

export default AncillaryStep
